import * as CL from '@design-system/component-library';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoaderData } from 'react-router';
import { authenticationMsg, deliveryDetailsMsg, t } from '../../../common/i18n/index.js';
import { getProductDeliveryTypesInCart } from '../../../common/utils/checkoutUtils.js';
import { deepEqual } from '../../../common/utils/objectUtils.js';
import { loadDeliveryMethods } from '../../../selfservice/actions/index.js';
import { EmployeeDeviceChange } from '../../DeviceCheckout/EmployeeDeviceChange.js';
import { PunchoutCheckoutAuth } from './PunchoutCheckoutAuth.js';
import { PunchoutCheckoutBilling } from './PunchoutCheckoutBilling.js';
import { PunchoutDeliveryDetails } from './PunchoutDeliveryDetails.js';
import type { PunchoutCheckoutLoaderData } from '../../../common/loaders.js';
import type { CLStepperItem } from '../../../common/types/componentLibrary';
import type { DeviceChangeRequest } from '../../../common/types/device.js';
import type { CommonError } from '../../../common/types/errors.js';
import type { EmployeeSubscription } from '../../../common/types/subscription.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { State } from '../../../selfservice/common/store.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type { PersonalBillingDetailsData } from '../../PersonalBillingDetails/PersonalBillingDetails.js';

type PunchoutCheckoutStepsProps = {
  activeStep: ActiveStep;
  billingInfoConfirmed: boolean;
  deliveryDetails: DeviceCheckoutDeliveryDetailsType;
  deviceSubs: EmployeeSubscription[];
  hideDeviceChangeStep: boolean;
  isBillingTermsAccepted?: boolean;
  isDeviceChangeStepDone: boolean;
  isOmalaiteLasku?: boolean;
  isStronglyAuthenticated?: boolean;
  errors?: CommonError[];
  onDeviceChangeNextStep: (updatedDeviceChangeRequest: DeviceChangeRequest) => void;
  onSaveBillingDetailsAndNext: (values: PersonalBillingDetailsData) => void;
  onSaveDeliveryDetailsAndNext: (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => void;
  onSetBillingTerms: (termsAccepted: boolean) => void;
  setDeliveryDetailsObj: (updatedDeliveryDetails: DeviceCheckoutDeliveryDetailsType) => void;
  skipDeliveryDetails: boolean;
  shipmentTypeSelectionEnabled: boolean;
  showDeliveryDetailsStep: boolean;
  skipDeliveryDetailsAndNext: () => void;
  user: AuthenticatedUser;
};

export enum ActiveStep {
  DEVICE_CHANGE = 'deviceChangeStep',
  AUTH = 'authStep',
  BILLING = 'billingStep',
  DELIVERY_DETAILS = 'deliveryDetailsStep',
}

export const PunchoutCheckoutSteps = ({
  activeStep,
  billingInfoConfirmed,
  deliveryDetails,
  deviceSubs,
  hideDeviceChangeStep,
  isBillingTermsAccepted,
  isDeviceChangeStepDone,
  isOmalaiteLasku,
  isStronglyAuthenticated,
  onDeviceChangeNextStep,
  onSaveBillingDetailsAndNext,
  onSaveDeliveryDetailsAndNext,
  onSetBillingTerms,
  setDeliveryDetailsObj,
  skipDeliveryDetails,
  shipmentTypeSelectionEnabled,
  showDeliveryDetailsStep,
  skipDeliveryDetailsAndNext,
  user,
}: PunchoutCheckoutStepsProps) => {
  const dispatch = useDispatch();
  const { models } = useLoaderData() as PunchoutCheckoutLoaderData;
  const cartItems = useSelector((state: State) => state.deviceCheckout?.cartItems || [], deepEqual);
  const deliveryTypesInCart = useMemo(() => getProductDeliveryTypesInCart(cartItems, models), [cartItems, models]);

  useEffect(() => {
    if (!skipDeliveryDetails) {
      dispatch(loadDeliveryMethods(deliveryTypesInCart));
    }
  }, [deliveryTypesInCart, dispatch, skipDeliveryDetails]);

  useEffect(() => {
    if (
      skipDeliveryDetails &&
      !showDeliveryDetailsStep &&
      !isOmalaiteLasku &&
      (isDeviceChangeStepDone || hideDeviceChangeStep)
    ) {
      skipDeliveryDetailsAndNext();
    }
  }, [
    skipDeliveryDetailsAndNext,
    isOmalaiteLasku,
    skipDeliveryDetails,
    showDeliveryDetailsStep,
    isDeviceChangeStepDone,
    hideDeviceChangeStep,
  ]);

  type StepperItems = CLStepperItem[];
  const deviceChangeSteps: StepperItems = hideDeviceChangeStep
    ? []
    : [
        {
          id: ActiveStep.DEVICE_CHANGE,
          name: t.JN29('Previous device'),
          content: <EmployeeDeviceChange user={user} onNextClick={onDeviceChangeNextStep} deviceSubs={deviceSubs} />,
          completed: isDeviceChangeStepDone,
        },
      ];

  const omaLaiteLaskuSteps: StepperItems = isOmalaiteLasku
    ? [
        {
          id: ActiveStep.AUTH,
          name: t.A00V(authenticationMsg),
          content: <PunchoutCheckoutAuth />,
          disabled: !isStronglyAuthenticated,
          completed: isStronglyAuthenticated,
        },
        {
          id: ActiveStep.BILLING,
          name: t.NMNN('Oma Laitelasku details'),
          content: isStronglyAuthenticated ? (
            <PunchoutCheckoutBilling
              user={user}
              onClickNext={onSaveBillingDetailsAndNext}
              isBillingTermsAccepted={isBillingTermsAccepted}
              onSetBillingTerms={onSetBillingTerms}
            />
          ) : undefined,
          disabled: !isStronglyAuthenticated,
          completed: billingInfoConfirmed,
        },
      ]
    : [];

  const restSteps: StepperItems = [
    {
      id: ActiveStep.DELIVERY_DETAILS,
      name: t.T78Y(deliveryDetailsMsg),
      content: (
        <PunchoutDeliveryDetails
          onSaveDeliveryDetailsAndNext={updatedDeliveryDetails => {
            if (showDeliveryDetailsStep) {
              skipDeliveryDetailsAndNext();
              return;
            }
            onSaveDeliveryDetailsAndNext(updatedDeliveryDetails);
          }}
          setDeliveryDetailsObj={setDeliveryDetailsObj}
          deliveryDetails={deliveryDetails}
          shipmentTypeSelectionEnabled={shipmentTypeSelectionEnabled}
          showDeliveryDetailsStep={showDeliveryDetailsStep}
        />
      ),
      disabled: !billingInfoConfirmed,
    },
    {
      id: 'summaryStep',
      name: t.VDB4('Summary'),
      disabled: true,
    },
    {
      id: 'confirmationStep',
      name: t.R69E('Confirmation'),
      disabled: true,
    },
  ];

  const steps: StepperItems = [...deviceChangeSteps, ...omaLaiteLaskuSteps, ...restSteps];

  return (
    <div className="cl-stepper-wrapper">
      <CL.Stepper steps={steps} activeStep={activeStep}></CL.Stepper>
    </div>
  );
};
